import React, { useEffect, useRef, useState } from "react";
import { API, controller, action } from "../api/api";

/**
 * Ten starting points, so the common case is one click rather than a colour wheel.
 *
 * They are not a safe list — the derivation corrects whatever it is given, and half of
 * these need correcting: teal, green, amber and orange all fail white text at these
 * values, and indigo and slate both fail to stand off a dark page. They are here because
 * a shop owner should not have to invent a hex, not because they are pre-approved.
 */
const PRESETS = [
  { hex: "#6675C2", name: "Indigo" },
  { hex: "#2563EB", name: "Blue" },
  { hex: "#0D9488", name: "Teal" },
  { hex: "#16A34A", name: "Green" },
  { hex: "#D97706", name: "Amber" },
  { hex: "#EA580C", name: "Orange" },
  { hex: "#DC2626", name: "Red" },
  { hex: "#E11D48", name: "Rose" },
  { hex: "#7C3AED", name: "Purple" },
  { hex: "#475569", name: "Slate" },
];

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Picks the organisation's brand colour, and shows what will actually ship.
 *
 * **The preview is the corrected colour, not the raw pick.** A single hex is not a theme:
 * the server widens it into a fill, its hover and pressed states, a subtle tint, the text
 * drawn on it and the accent used as text, adjusting lightness until each is legible. Those
 * differ from the raw choice whenever the choice cannot carry a readable label — neon yellow
 * comes back a darker gold with black text. Showing the pick rather than the result would
 * mean the first time anyone saw the real colour was on a counter.
 *
 * Preview is debounced and served by GET api/Org/PreviewTheme, so the same code decides here
 * and at save time. Deriving it in the browser instead would be a second implementation of
 * contrast arithmetic, and the two would drift.
 *
 * Follows the controls convention: `onChange` receives a synthetic `{ target: { name, value } }`
 * so it drops straight into an existing `handleInpChg`.
 */
const InputColor = ({ name, label, value, onChange, hint }) => {
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const current = HEX.test(value || "") ? value.toUpperCase() : "";

  // Debounced, because a colour input fires continuously while a pointer is dragged and
  // every keystroke in the hex box is a change. 250ms is below noticing and well above
  // one request per pixel.
  useEffect(() => {
    if (!current) {
      setPreview(null);
      return undefined;
    }

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // getById, and the hash stripped: the helper appends a trailing slash to every URL,
      // so a "?seed=" query would arrive as "...?seed=%23ABC/" and 400. The endpoint takes
      // a bare six-digit path segment for exactly that reason.
      API(controller.Org + action.PreviewTheme).getById(
        current.replace("#", ""),
        (res) => setPreview(res || null)
      );
    }, 250);

    return () => clearTimeout(timer.current);
  }, [current]);

  const emit = (hex) => onChange && onChange({ target: { name, value: hex } });

  const light = preview?.light;

  // "Corrected" only when the fill actually differs from the pick. Saying so on every
  // colour would train people to ignore it.
  const corrected = light?.fill && current && light.fill.toUpperCase() !== current;

  return (
    <div className="flex flex-col gap-[8px]">
      {label && <span className="text-[13px] text-gray-600">{label}</span>}

      <div className="flex flex-wrap items-center gap-[6px]">
        {PRESETS.map((p) => (
          <button
            key={p.hex}
            type="button"
            title={p.name}
            aria-label={p.name}
            onClick={() => emit(p.hex)}
            style={{ backgroundColor: p.hex }}
            className={`w-[26px] h-[26px] rounded-full border-2 transition-transform hover:scale-110 ${
              current === p.hex ? "border-gray-800" : "border-transparent"
            }`}
          />
        ))}
      </div>

      <div className="flex items-center gap-[10px]">
        {/* The OS picker, for a brand that is not on the list. Native, so no dependency. */}
        <input
          type="color"
          value={current || "#6675C2"}
          onChange={(e) => emit(e.target.value.toUpperCase())}
          className="w-[42px] h-[32px] p-0 border border-gray-300 rounded cursor-pointer bg-transparent"
          aria-label={label || "Primary colour"}
        />

        <input
          type="text"
          value={value || ""}
          onChange={(e) => emit(e.target.value)}
          placeholder="#6675C2"
          maxLength={7}
          spellCheck={false}
          className="w-[110px] h-[32px] px-[8px] border border-gray-300 rounded font-mono text-[13px] uppercase"
        />

        {/* Clearing is as easy as choosing. An organisation that changes its mind must be
            able to get back to the product default without knowing what that hex is. */}
        <button
          type="button"
          onClick={() => emit("")}
          className="text-[12px] text-gray-500 underline hover:text-gray-800"
        >
          Use the default
        </button>
      </div>

      {/* What will actually ship: the corrected fill with its real label colour on it. */}
      {light?.fill && (
        <div className="flex items-center gap-[10px]">
          <span
            className="inline-flex items-center justify-center h-[34px] px-[18px] rounded-[8px] text-[13px] font-semibold"
            style={{ backgroundColor: light.fill, color: light.onFill }}
          >
            Save Changes
          </span>
          <span className="text-[11px] text-gray-500">
            {corrected
              ? `Adjusted to ${light.fill} so the label stays readable.`
              : "Preview"}
          </span>
        </div>
      )}

      {hint && <span className="text-[11px] text-gray-400">{hint}</span>}
    </div>
  );
};

export default InputColor;
